// Bloco "Storage" do painel Tecnologia. Pasta de documentos (PDFs etc.) do CRM:
// listar, baixar e enviar. Card auto-contido — a lista rola dentro dele, então o
// painel não ganha rolagem. Backend: /api/docs (list/download/upload/delete).
import { useState, useEffect, useCallback, useRef } from 'react'
import { FiHardDrive, FiDownload, FiUploadCloud, FiTrash2, FiLoader, FiFile } from 'react-icons/fi'
import { API_ENDPOINTS } from '../lib/api'

type Fetch = (input: string, init?: RequestInit) => Promise<Response>
type DocFile = { name: string; size: number; mtime: number }

const fmtSize = (n: number) => {
  if (!n) return '—'
  const u = ['B', 'KB', 'MB', 'GB']; let i = 0, v = n
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i++ }
  return `${v.toFixed(v < 10 && i > 0 ? 1 : 0)} ${u[i]}`
}
const fmtDate = (ts: number) => { try { return new Date(ts * 1000).toLocaleDateString('pt-BR') } catch { return '—' } }

export function DocStorage({ authenticatedFetch, isAdmin }: { authenticatedFetch: Fetch; isAdmin: boolean }) {
  const [files, setFiles] = useState<DocFile[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [uploading, setUploading] = useState(false)
  const [drag, setDrag] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const load = useCallback(async () => {
    setError(null)
    try {
      const r = await authenticatedFetch(API_ENDPOINTS.DOCS)
      const d = await r.json()
      if (!r.ok || !d.ok) throw new Error(d.error || `HTTP ${r.status}`)
      setFiles(d.files)
    } catch (e) { setError((e as Error).message); setFiles([]) }
  }, [authenticatedFetch])
  useEffect(() => { load() }, [load])

  const download = async (name: string) => {
    try {
      const r = await authenticatedFetch(`${API_ENDPOINTS.DOCS_DOWNLOAD}?name=${encodeURIComponent(name)}`)
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      const url = URL.createObjectURL(await r.blob())
      const a = document.createElement('a')
      a.href = url; a.download = name; document.body.appendChild(a); a.click()
      a.remove(); URL.revokeObjectURL(url)
    } catch (e) { setError(`Falha ao baixar: ${(e as Error).message}`) }
  }

  const upload = async (list: FileList | File[] | null) => {
    if (!list || !list.length) return
    setUploading(true); setError(null)
    try {
      for (const file of Array.from(list)) {
        const fd = new FormData(); fd.append('file', file)
        const r = await authenticatedFetch(API_ENDPOINTS.DOCS_UPLOAD, { method: 'POST', body: fd })
        const d = await r.json()
        if (!r.ok || !d.ok) throw new Error(d.error || `HTTP ${r.status}`)
      }
      await load()
    } catch (e) { setError(`Falha ao enviar: ${(e as Error).message}`) }
    finally { setUploading(false) }
  }

  const del = async (name: string) => {
    if (!window.confirm(`Remover "${name}" do storage?`)) return
    try {
      const r = await authenticatedFetch(`${API_ENDPOINTS.DOCS}?name=${encodeURIComponent(name)}`, { method: 'DELETE' })
      const d = await r.json()
      if (!r.ok || !d.ok) throw new Error(d.error || `HTTP ${r.status}`)
      await load()
    } catch (e) { setError(`Falha ao remover: ${(e as Error).message}`) }
  }

  return (
    <div className="glass-panel neu-bar neu-forms rounded-2xl p-4 h-full flex flex-col min-h-0">
      <div className="flex items-center gap-2 mb-3 shrink-0">
        <FiHardDrive className="w-4 h-4 text-primary shrink-0" />
        <span className="text-sm font-semibold text-foreground flex-1">Storage de documentos</span>
        <span className="text-xs text-muted-foreground tabular-nums">{files?.length ?? 0} arquivos</span>
        {isAdmin && (
          <button onClick={() => inputRef.current?.click()} disabled={uploading}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-medium bg-primary text-primary-foreground shadow-sm hover:opacity-90 disabled:opacity-60 shrink-0">
            {uploading ? <FiLoader className="w-4 h-4 animate-spin" /> : <FiUploadCloud className="w-4 h-4" />}
            <span className="hidden sm:inline">{uploading ? 'Enviando…' : 'Enviar'}</span>
          </button>
        )}
        <input ref={inputRef} type="file" multiple className="hidden"
          accept=".pdf,.md,.txt,.docx,.doc,.xlsx,.xlsb,.xls,.pptx,.ppt,.csv,.ofx,.qfx,.qif,.png,.jpg,.jpeg,.gif,.mp4,.mov,.zip"
          onChange={e => { const f = Array.from(e.target.files || []); e.target.value = ''; upload(f) }} />
      </div>

      {error && <div className="mb-2 px-3 py-2 rounded-lg bg-destructive/10 text-destructive text-xs shrink-0">{error}</div>}

      <div
        onDragOver={isAdmin ? (e => { e.preventDefault(); setDrag(true) }) : undefined}
        onDragLeave={() => setDrag(false)}
        onDrop={isAdmin ? (e => { e.preventDefault(); setDrag(false); upload(e.dataTransfer.files) }) : undefined}
        className={`flex-1 min-h-0 overflow-y-auto rounded-xl border border-dashed transition-colors ${drag ? 'border-primary bg-primary/5' : 'border-border/60'}`}>
        {files === null && (
          <div className="flex items-center justify-center gap-2 py-10 text-muted-foreground text-sm">
            <FiLoader className="w-4 h-4 animate-spin" /> Carregando…
          </div>
        )}
        {files && files.length === 0 && (
          <div className="py-10 text-center text-muted-foreground text-sm px-6">
            {isAdmin ? 'Vazio. Arraste PDFs aqui ou clique em Enviar.' : 'Nenhum documento.'}
          </div>
        )}
        {files && files.length > 0 && (
          <ul className="divide-y divide-border/40">
            {files.map(f => (
              <li key={f.name} className="flex items-center gap-3 px-3 py-2.5 hover:bg-muted/40 transition-colors">
                <FiFile className="w-4 h-4 shrink-0 text-red-500/80" />
                <button onClick={() => download(f.name)} title={`Baixar ${f.name}`}
                  className="flex-1 min-w-0 text-left text-sm text-foreground truncate hover:text-primary">{f.name}</button>
                <span className="text-xs text-muted-foreground tabular-nums shrink-0 hidden sm:block">{fmtSize(f.size)} · {fmtDate(f.mtime)}</span>
                <button onClick={() => download(f.name)} title="Baixar"
                  className="p-1.5 rounded-lg text-muted-foreground hover:text-primary hover:bg-muted/60 shrink-0">
                  <FiDownload className="w-4 h-4" />
                </button>
                {isAdmin && (
                  <button onClick={() => del(f.name)} title="Remover"
                    className="p-1.5 rounded-lg text-muted-foreground hover:text-destructive hover:bg-muted/60 shrink-0">
                    <FiTrash2 className="w-4 h-4" />
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}
